// The org's date settings, fetched once and shared.
//
// Why a hook and not a shared column-list constant: `ORG_HOURS_COLUMNS` in
// orgHours.ts was exactly that experiment, and has since been deleted. Its doc
// comment said "keeps the four call sites in step"; it ended up with zero
// importers, all four sites inlining the literal, and one of them already drifted. TimesheetsScreen never selected
// `week_start` because nothing made it — a constant you have to remember to use
// is not a mechanism. A screen cannot forget to select a column it never selects.
//
// See docs/UI-CONVENTIONS.md §2.
import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { DEFAULT_DATE_FORMAT, weekStartIndex } from './dates';
import { orgHoursFrom, DEFAULT_ORG_HOURS, type OrgHours, type OrgHoursRow } from './orgHours';
import type { Workdays } from './workdays';
import type { EditDayRollout } from './editDay';

/** R3 employment standards. The values a hire is set up against. */
export type Employment = {
  probation_months: number;
  notice_days_employee: number;
  notice_days_company: number;
  offer_acceptance_days: number;
  email_format: string;
  reminder_lead_days: number;
};

/** policy.calendar.types. Events store the id; an archived type stays on the events already filed under it. */
export type EventType = { id: string; label: string; archived?: boolean };

export type OrgDates = {
  /** getDay() index the week starts on (0=Sun, 1=Mon). */
  weekStartsOn: number;
  /** IANA zone. THE calendar for anything meaning "a day" or "a week". */
  tz: string;
  /** org_settings.date_format, for fmtDate(value, dateFormat). Passed as stored:
   *  fmtDate falls back to the column default on a value it doesn't know. */
  dateFormat: string;
  /** The org's standard day/week. The fallback for anyone with no contract. */
  hours: OrgHours;
  /** Which days the org works — the input to every workday count. */
  workdays: Workdays | null;
  /** policy.employment — probation, notice, the company address format. */
  employment: Employment;
  /** policy.calendar.types — every event type, archived included. */
  eventTypes: EventType[];
  /** policy.calendar.links — false turns every personal subscription link off. */
  calendarLinks: boolean;
  /** policy.editDay.rollout — which time-correction dialog shows (20261103120000). */
  editDay: EditDayRollout;
};

// Every value here is the COLUMN default, not a guess:
//   week_start 'Sun'       → 20260604010000_modules.sql:157
//   tz_iana 'Asia/Kolkata' → 20260614000000_time_integrity.sql:19
//   date_format 'DD-MM-YYYY' → 20260604010000_modules.sql:158
//   09:30-18:30 less 60m   → 20260604010000_modules.sql:160-162 (= 8h/40h)
//   workdays Mon-Fri       → 20260604010000_modules.sql:159
//   policy.employment      → 20260828110000_employment_policy.sql:40-49
//   policy.calendar.types  → 20260917120000_calendar_events.sql:49-55
//   policy.calendar.links  → absent means on (20260918100000_calendar_links.sql:52-56)
//   policy.editDay.rollout → 'off' (20261103120000_edit_day.sql)
// so the pre-fetch render agrees with a freshly-seeded org instead of
// contradicting it. This is the ONE place a default may exist.
export const DEFAULT_EMPLOYMENT: Employment = {
  probation_months: 3,
  notice_days_employee: 7,
  notice_days_company: 7,
  offer_acceptance_days: 7,
  email_format: '{first}.{last}@hyprflow.com',
  reminder_lead_days: 14,
};

export const DEFAULT_EVENT_TYPES: EventType[] = [
  { id: 'meeting', label: 'Meeting' },
  { id: 'company_event', label: 'Company event' },
  { id: 'training', label: 'Training' },
  { id: 'deadline', label: 'Deadline' },
  { id: 'celebration', label: 'Celebration' },
  { id: 'other', label: 'Other' },
];

export const DEFAULT_ORG_DATES: OrgDates = {
  weekStartsOn: 0,
  tz: 'Asia/Kolkata',
  dateFormat: DEFAULT_DATE_FORMAT,
  hours: DEFAULT_ORG_HOURS,
  workdays: null,
  employment: DEFAULT_EMPLOYMENT,
  eventTypes: DEFAULT_EVENT_TYPES,
  calendarLinks: true,
  editDay: 'off',
};

type Row =
  | ({
      week_start?: string | null;
      tz_iana?: string | null;
      date_format?: string | null;
      policy?: {
        employment?: Partial<Employment>;
        calendar?: { types?: EventType[]; links?: boolean };
        editDay?: { rollout?: EditDayRollout };
      } | null;
    } & OrgHoursRow)
  | null;

let cache: Promise<OrgDates> | null = null;

const load = (): Promise<OrgDates> => {
  if (cache) return cache;
  const sb = supabase;
  // Promise.resolve because PostgREST's builder is a thenable, not a Promise.
  cache = !sb
    ? Promise.resolve(DEFAULT_ORG_DATES)
    : Promise.resolve(
        sb
          .from('org_settings')
          .select(
            'week_start, tz_iana, date_format, day_start, day_end, lunch_mins, workdays, policy',
          )
          .maybeSingle(),
      ).then(({ data }): OrgDates => {
        const row = data as Row;
        return {
          weekStartsOn: weekStartIndex(row?.week_start),
          tz: row?.tz_iana || DEFAULT_ORG_DATES.tz,
          dateFormat: row?.date_format || DEFAULT_ORG_DATES.dateFormat,
          hours: row ? orgHoursFrom(row) : DEFAULT_ORG_HOURS,
          workdays: row?.workdays ?? null,
          employment: { ...DEFAULT_EMPLOYMENT, ...(row?.policy?.employment ?? {}) },
          eventTypes: row?.policy?.calendar?.types ?? DEFAULT_EVENT_TYPES,
          calendarLinks: row?.policy?.calendar?.links !== false,
          editDay: row?.policy?.editDay?.rollout ?? 'off',
        };
      });
  return cache;
};

/** Call after Org Settings writes any column this selects, or the app keeps the old one. */
export const resetOrgDates = () => {
  cache = null;
};

export const useOrgDates = (): OrgDates => {
  const [v, setV] = useState(DEFAULT_ORG_DATES);
  useEffect(() => {
    let live = true;
    void load().then((d) => live && setV(d));
    return () => {
      live = false;
    };
  }, []);
  return v;
};
